<template>
  <div
    class="bg-surface-canvas dark:bg-surface-level-900 dashboard-layout flex flex-col min-h-screen"
  >
    <BaseDrawer
      :model-value="sidebarOpen && isMobileViewport"
      :title="$t('common.accessibility.mainMenu')"
      max-width-class="sm:max-w-xs"
      side="left"
      @close="closeSidebar"
    >
      <template #title>
        <Logo
          :to="logoLink"
          wrapper-class="-m-1.5 p-1.5"
          height-class="h-8 sm:h-8"
          alt=""
        />

        <span class="sr-only">{{ $t('common.accessibility.mainMenu') }}</span>
      </template>

      <div class="flex flex-col gap-y-5 min-h-full">
        <div
          v-if="showOrganizationBadge"
          class="bg-surface-level-50 border border-border-level-200 dark:bg-surface-canvas/5 dark:border-border-inverse/10 px-3 py-2.5 rounded-lg"
        >
          <p
            class="dark:text-content-level-500 font-medium text-[10px] text-content-level-400 tracking-wider uppercase"
          >
            {{ $t('common.dashboard.organization') }}
          </p>

          <div class="flex gap-x-2 items-center mt-0.5">
            <BuildingOffice2Icon
              class="dark:text-content-level-500 shrink-0 size-4 text-content-level-400"
              aria-hidden="true"
            />

            <span
              class="dark:text-content-inverse font-semibold text-content-level-900 text-sm truncate"
              :title="organizationName"
              >{{ organizationName }}</span
            >
          </div>
        </div>

        <nav class="flex flex-1 flex-col relative">
          <ul
            role="list"
            class="flex flex-1 flex-col gap-y-7"
          >
            <li>
              <SidebarMenu
                :menu-items="primaryMenus"
                :all-menu-items="allMenuItems"
                variant="mobile"
                :loading="isLoadingMenus"
                :initialized="isMenusInitialized"
              />
            </li>

            <li v-if="isLoadingMenus || !isMenusInitialized || secondaryMenus.length > 0">
              <div
                v-if="isLoadingMenus || !isMenusInitialized"
                class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-3 mb-2 rounded w-12"
              />

              <div
                v-else
                class="font-semibold text-content-level-400 text-xs/6"
              >
                {{ $t('common.dashboard.management') }}
              </div>

              <SidebarMenu
                :menu-items="secondaryMenus"
                :all-menu-items="allMenuItems"
                variant="mobile"
                :loading="isLoadingMenus"
                :initialized="isMenusInitialized"
                :skeleton-count="3"
              />
            </li>

            <li
              v-if="isLoadingMenus || !isMenusInitialized || userMenus.length > 0"
              class="mt-auto"
            >
              <SidebarMenu
                :menu-items="userMenus"
                :all-menu-items="allMenuItems"
                variant="mobile"
                :loading="isLoadingMenus"
                :initialized="isMenusInitialized"
                :skeleton-count="2"
              />
            </li>
          </ul>
        </nav>
      </div>
    </BaseDrawer>

    <!-- Static sidebar for desktop -->
    <div
      class="dark:bg-surface-level-900 hidden lg:fixed lg:flex lg:flex-col lg:inset-y-0 lg:w-72 lg:z-(--z-index-ds-rail)"
    >
      <!-- Sidebar component, swap this element with another sidebar if you like -->
      <div
        class="bg-surface-canvas border-border-level-200 border-r dark:bg-surface-scrim/10 dark:border-border-inverse/10 flex flex-col gap-y-5 grow overflow-y-auto pb-4 px-6"
      >
        <div class="flex h-16 items-center shrink-0">
          <Logo
            :to="logoLink"
            wrapper-class="-m-1.5 p-1.5"
            height-class="h-8 sm:h-8"
            alt=""
          />
        </div>

        <div
          v-if="showOrganizationBadge"
          class="bg-surface-level-50 border border-border-level-200 dark:bg-surface-canvas/5 dark:border-border-inverse/10 px-3 py-2.5 rounded-lg"
        >
          <p
            class="dark:text-content-level-500 font-medium text-[10px] text-content-level-400 tracking-wider uppercase"
          >
            {{ $t('common.dashboard.organization') }}
          </p>

          <div class="flex gap-x-2 items-center mt-0.5">
            <BuildingOffice2Icon
              class="dark:text-content-level-500 shrink-0 size-4 text-content-level-400"
              aria-hidden="true"
            />

            <span
              class="dark:text-content-inverse font-semibold text-content-level-900 text-sm truncate"
              :title="organizationName"
              >{{ organizationName }}</span
            >
          </div>
        </div>

        <nav class="flex flex-1 flex-col">
          <ul
            role="list"
            class="flex flex-1 flex-col gap-y-7"
          >
            <li>
              <SidebarMenu
                :menu-items="primaryMenus"
                :all-menu-items="allMenuItems"
                variant="desktop"
                :loading="isLoadingMenus"
                :initialized="isMenusInitialized"
              />
            </li>

            <li v-if="isLoadingMenus || !isMenusInitialized || secondaryMenus.length > 0">
              <div
                v-if="isLoadingMenus || !isMenusInitialized"
                class="animate-pulse bg-surface-level-200 dark:bg-surface-level-700 h-3 mb-2 rounded w-12"
              />

              <div
                v-else
                class="font-semibold text-content-level-400 text-xs/6"
              >
                {{ $t('common.dashboard.management') }}
              </div>

              <SidebarMenu
                :menu-items="secondaryMenus"
                :all-menu-items="allMenuItems"
                variant="desktop"
                :loading="isLoadingMenus"
                :initialized="isMenusInitialized"
                :skeleton-count="3"
              />
            </li>

            <li
              v-if="isLoadingMenus || !isMenusInitialized || userMenus.length > 0"
              class="mt-auto"
            >
              <SidebarMenu
                :menu-items="userMenus"
                :all-menu-items="allMenuItems"
                variant="desktop"
                :loading="isLoadingMenus"
                :initialized="isMenusInitialized"
                :skeleton-count="2"
              />
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div class="flex flex-1 flex-col lg:pl-72 min-h-0">
      <div class="flex-shrink-0 sticky top-0 w-full z-(--z-index-ds-sticky)">
        <div class="lg:max-w-7xl lg:mx-auto lg:px-8">
          <div
            class="bg-surface-canvas dark:bg-surface-level-900 dark:shadow-none flex gap-x-4 h-16 items-center lg:px-0 lg:shadow-none px-4 shadow-xs sm:gap-x-6 sm:px-6"
          >
            <button
              type="button"
              class="-m-2.5 dark:hover:text-content-inverse dark:text-content-level-400 hover:text-content-level-900 lg:hidden p-2.5 text-content-level-700"
              @click="openSidebar"
            >
              <span class="sr-only">
                {{ $t('common.accessibility.openSidebar') }}
              </span>

              <Bars3Icon
                class="size-6"
                aria-hidden="true"
              />
            </button>

            <!-- Separator -->
            <div
              class="bg-surface-level-200 dark:bg-surface-level-700 h-6 lg:hidden w-px"
              aria-hidden="true"
            />

            <div
              class="flex flex-1 gap-x-4 items-center justify-between lg:gap-x-6 min-w-0 self-stretch"
            >
              <DashboardGlobalSearch />

              <div class="2xl:gap-x-8 flex gap-x-4 items-center shrink-0">
                <!-- Cart Trigger (only renders when there are cart items) -->
                <DraftOrdersTrigger
                  :trigger-button-class="dashboardActionButtonClass"
                  :trigger-icon-class="dashboardCartIconClass"
                  :trigger-badge-class="dashboardActionBadgeClass"
                />

                <NotificationsDrawer
                  :items="notificationsStore.items"
                  :unread-count="notificationsStore.unreadCount"
                  :onboarding-tasks="urgentOnboardingTasks"
                  :is-loaded="notificationsStore.isLoaded"
                  :model-value="notificationsStore.isPanelOpen"
                  :is-connecting="notificationsStore.isConnecting"
                  :is-fetching-more="notificationsStore.isFetchingMore"
                  :is-deleting="isDeletingNotifications"
                  :delete-success-key="notificationDeleteSuccessKey"
                  :has-next-page="notificationsStore.hasNextPage"
                  :last-received-at="notificationsStore.lastReceivedAt"
                  :error="notificationsStore.error"
                  :trigger-button-class="dashboardActionButtonClass"
                  :trigger-icon-class="dashboardActionIconClass"
                  :trigger-badge-class="dashboardActionBadgeClass"
                  @update:model-value="handleNotificationsPanelUpdate"
                  @mark-all-read="handleMarkAllNotificationsAsRead"
                  @delete="handleDeleteNotifications"
                  @notification-selected="handleNotificationSelect"
                  @select-onboarding-task="handleOnboardingTaskSelect"
                  @load-more="handleLoadMoreNotifications"
                />

                <!-- Separator -->
                <div
                  class="dark:lg:bg-surface-canvas/10 hidden lg:bg-surface-level-200 lg:block lg:h-6 lg:w-px"
                  aria-hidden="true"
                />

                <!-- Profile dropdown -->
                <UDropdownMenu
                  :items="profileMenuItems"
                  :content="profileMenuContent"
                  :ui="profileMenuUi"
                >
                  <button
                    type="button"
                    class="flex items-center relative"
                  >
                    <span class="-inset-1.5 absolute" />

                    <span class="sr-only">
                      {{ $t('common.accessibility.openUserMenu') }}
                    </span>

                    <img
                      class="-outline-offset-1 bg-surface-level-50 dark:bg-surface-level-800 dark:outline-focus-inverse/10 object-cover outline outline-focus-ink/5 rounded-full size-8"
                      :src="userAvatarSrc"
                      :alt="$t('dashboard.labels.userAvatar')"
                    />

                    <span class="hidden lg:flex lg:items-center">
                      <ClientOnly fallback-tag="span">
                        <span
                          class="dark:text-content-inverse font-semibold ml-4 text-content-level-900 text-sm/6"
                          aria-hidden="true"
                        >
                          {{ dashboardUserName }}
                        </span>

                        <template #fallback>
                          <span
                            class="dark:text-content-inverse font-semibold ml-4 text-content-level-900 text-sm/6"
                            aria-hidden="true"
                            >{{ $t('common.dashboard.account') }}</span
                          >
                        </template>
                      </ClientOnly>

                      <ChevronDownIcon
                        class="ml-2 size-5 text-content-level-400"
                        aria-hidden="true"
                      />
                    </span>
                  </button>
                </UDropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main class="flex-1 min-h-0 py-4 sm:py-10">
        <div class="lg:px-0 max-w-7xl mx-auto px-4 sm:px-0">
          <ClientOnly>
            <template #fallback>
              <div class="flex items-center justify-center min-h-[calc(100vh-400px)]">
                <SharedLoading
                  :title="$t('dashboard.labels.settingUpYourWorkspace')"
                  :description="$t('dashboard.messages.loadingYourMenuAndVerifyingPermissions')"
                />
              </div>
            </template>

            <PageErrorBoundary>
              <Transition
                appear
                enter-active-class="transition-opacity duration-500 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
              >
                <div class="py-6">
                  <slot />
                </div>
              </Transition>
            </PageErrorBoundary>
          </ClientOnly>
        </div>
      </main>

      <div class="lg:px-8 max-w-7xl mt-auto mx-auto px-4 sm:px-6 w-full">
        <DashboardFooter />
      </div>
    </div>

    <!-- Welcome modal after partner onboarding -->
    <DashboardWelcomeModal
      :open="showWelcomeModal"
      :user-name="welcomeModalUserName"
      @close="closeWelcomeModal"
    />

    <!-- Draft Orders Drawer -->
    <DraftOrdersDrawer
      :is-open="isDraftOrdersOpen"
      @close="closeDraftOrdersCart"
    />

    <!-- Menu Support Widget (lazy-loaded) -->
    <ClientOnly>
      <MenuSupportWidget />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import { Bars3Icon, BuildingOffice2Icon } from '@heroicons/vue/24/outline'
import { computed, onMounted, ref, watch } from 'vue'
import BaseDrawer from '~/components/shared/dialogs/BaseDrawer.vue'
import { useLogger } from '~/composables/useLogger'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import DashboardGlobalSearch from '~/components/dashboard/DashboardGlobalSearch.vue'
import NotificationsDrawer from '~/components/dashboard/drawers/NotificationsDrawer.vue'
import DraftOrdersDrawer from '~/components/DraftOrdersDrawer.vue'
import DraftOrdersTrigger from '~/components/DraftOrdersTrigger.vue'
import SidebarMenu from '~/components/dashboard/SidebarMenu.vue'
import PageErrorBoundary from '~/components/PageErrorBoundary.vue'
import SharedLoading from '~/components/shared/Loaders/Loading.vue'
import MenuSupportWidget from '~/components/menu/MenuSupportWidget.vue'
import { useDashboardLayoutState } from '~/composables/useDashboardLayoutState'
import { useNavAuthMenu } from '~/composables/useNavAuthMenu'
import { hasText } from '~/utils/hasText'
import { Normalization } from '~/utils/normalization'
import { useLogoLink } from '~/composables/useLogoLink'

const isRecord = Normalization.isRecord

const logger = useLogger('DashboardLayout')

defineSlots<{
  default?: () => unknown
}>()

useHead({ title: 'Dashboard | Mealz' })

const route = useRoute()
const router = useRouter()
const { identity, isAuthenticated, logout } = useIdentity()
const {
  hasPartnerAccess,
  hasStoreAccess,
  isInternalStaff,
  user,
  organizerAccount,
  isOrganizer,
  initialized,
  loading,
  fetchUserAccess,
  clearUserState,
  isDraftOrdersOpen,
  closeDraftOrdersCart
} = useDashboardLayoutState()
const dashboardActionButtonClass =
  'relative inline-flex size-10 items-center justify-center rounded-full text-content-level-400 transition-colors duration-300 hover:bg-surface-level-100 hover:text-content-level-600 dark:hover:bg-surface-canvas/5 dark:hover:text-content-inverse cursor-pointer'
const dashboardActionIconClass = 'size-6'
const dashboardCartIconClass = `${dashboardActionIconClass} -translate-y-0.5`
const dashboardActionBadgeClass =
  'absolute right-0 top-0 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[12px] font-semibold leading-none text-content-inverse shadow-sm'
const {
  notificationsStore,
  isDeletingNotifications,
  notificationDeleteSuccessKey,
  handleMarkAllNotificationsAsRead,
  handleNotificationSelect,
  handleLoadMoreNotifications,
  handleDeleteNotifications,
  resetNotifications
} = useNotificationsNav()
const {
  isLoaded: areQuickActionsLoaded,
  load: loadQuickActions,
  reset: resetQuickActions,
  urgentTasks: urgentOnboardingTasks
} = useDashboardQuickActions()
// Internal staff (admin/super_admin) carry the unrestricted permission set,
// so hasPartnerAccess/hasStoreAccess read true for them too -- but quick
// actions calls a route RedirectSuperAdminToAdminPanel blocks and logs them
// out of (APP-1676/1688), so they're excluded here rather than at the store
// level, where hasPartnerAccess/hasStoreAccess keep their broader meaning.
const canShowOnboardingTasks = computed(
  () => (hasPartnerAccess.value || hasStoreAccess.value) && !isInternalStaff.value
)

const sidebarOpen = ref(false)
const isDesktop = useMediaQuery('(min-width: 1024px)')
const isMobileViewport = computed(() => !isDesktop.value)

const openSidebar = () => {
  sidebarOpen.value = true
}

const closeSidebar = () => {
  sidebarOpen.value = false
}

watch(isMobileViewport, (mobile) => {
  if (!mobile) closeSidebar()
})

const handleNotificationsPanelUpdate = (isOpen: boolean) => {
  notificationsStore.setPanelOpen(isOpen)

  if (isOpen && canShowOnboardingTasks.value) {
    void loadQuickActions(true)
  }
}

const handleOnboardingTaskSelect = async (task: { route: string }) => {
  notificationsStore.setPanelOpen(false)
  await navigateTo(task.route)
}

const isLoadingMenus = ref(true)
const isMenusInitialized = ref(false)
// Derived, not latched: a freshly promoted partner can reach the dashboard
// before `hasPartnerAccess` resolves, and latching would drop the modal for them.
const welcomeRequested = ref(false)
const showWelcomeModal = computed(() => welcomeRequested.value && hasPartnerAccess.value)
onMounted(() => {
  if (
    import.meta.client &&
    (route.query['welcome'] === '1' || sessionStorage.getItem('partnerOnboardingWelcome') === '1')
  ) {
    welcomeRequested.value = true
  }
})
watch(
  () => route.query['welcome'],
  (q) => {
    if (q === '1') welcomeRequested.value = true
  },
  { immediate: false }
)

watch(
  () => route.fullPath,
  () => {
    sidebarOpen.value = false

    if (areQuickActionsLoaded.value && canShowOnboardingTasks.value) {
      void loadQuickActions(true)
    }
  }
)

watch(
  () => [isAuthenticated.value, canShowOnboardingTasks.value] as const,
  ([authenticated, canShowTasks]) => {
    if (authenticated && canShowTasks) {
      void loadQuickActions()
      return
    }

    resetQuickActions()
  },
  { immediate: true }
)
async function closeWelcomeModal(): Promise<void> {
  welcomeRequested.value = false
  if (import.meta.client) {
    sessionStorage.removeItem('partnerOnboardingWelcome')
    if (route.query['welcome'] === '1') {
      const query = { ...route.query }
      delete query['welcome']
      await router.replace({ path: route.path, query })
    }
  }
}

const getFirstText = (...values: (string | null | undefined)[]): string | null => {
  for (const value of values) {
    if (hasText(value)) return value
  }

  return null
}

/** First name only for the post-onboarding welcome modal headline. */
const welcomeModalUserName = computed(() => {
  const userRecord = getUserRecord(user.value)
  const firstName = getString(userRecord?.['first_name'])?.trim() ?? ''
  if (firstName.length > 0) return firstName

  const name = getString(userRecord?.['name'])?.trim() ?? ''
  if (name.length === 0) return ''
  return name.split(/\s+/)[0] ?? ''
})

const { primaryMenus, secondaryMenus, userMenus } = useNavAuthMenu()

const getUserRecord = (storedUser: unknown): Record<string, unknown> | null => {
  const currentIdentity = identity.value
  if (currentIdentity !== null) return { ...currentIdentity }

  return isRecord(storedUser) ? storedUser : null
}

const getString = (value: unknown): string | null => (typeof value === 'string' ? value : null)

// User avatar URL - from Sanctum identity or the user store, with fallback to initials
const userAvatarSrc = computed(() => {
  const userRecord = getUserRecord(user.value)
  const name = getFirstText(getString(userRecord?.['name'])) ?? 'User'
  const avatar = getFirstText(
    getString(userRecord?.['avatar']),
    getString(userRecord?.['image_url']),
    getString(userRecord?.['profile_photo']),
    getString(userRecord?.['profile_picture'])
  )
  if (avatar !== null) return avatar
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=5754e8&color=ffffff`
})

// All menu items combined (for active state checking)
const allMenuItems = computed(() => {
  return [...primaryMenus.value, ...secondaryMenus.value, ...userMenus.value]
})

const dashboardUserName = computed(() => {
  const storedUser = isRecord(user.value) ? user.value : null

  return getFirstText(getString(identity.value?.name), getString(storedUser?.name)) ?? 'Account'
})

const { logoLink } = useLogoLink()
const t = usePresentationTranslator()

// Organization badge (shown below the logo when the user belongs to an organization)
const organizationName = computed(() => organizerAccount.value?.name ?? '')
const showOrganizationBadge = computed(() => isOrganizer.value && organizationName.value.length > 0)

// Handle logout
const handleLogout = async () => {
  try {
    await resetNotifications()
    await logout()
    clearUserState()
    await navigateTo('/')
  } catch (error) {
    logger.error('handleLogout', { error })
  }
}

const profileMenuContent = { align: 'end' } as const
const profileMenuUi = { content: 'w-48' } as const

const profileMenuItems = computed<DropdownMenuItem[]>(() => [
  ...userMenus.value.map((item) => ({ label: item.label, to: item.route })),
  { color: 'error', label: t('common.actions.signOut'), onSelect: () => void handleLogout() }
])

// Fetch user access data when layout mounts if user is authenticated
onMounted(async () => {
  if (isAuthenticated.value && !initialized.value) {
    try {
      await fetchUserAccess()
    } finally {
      isLoadingMenus.value = false
      isMenusInitialized.value = true
    }
  } else {
    isLoadingMenus.value = loading.value
    isMenusInitialized.value = initialized.value
  }
})
</script>
